import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  TouchableOpacity,
  StyleSheet,
  Platform,
  Modal,
  Text,
  TextInput as RNTextInput,
  ScrollView,
} from 'react-native';
import { QText, TextInput } from '../index';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'react-native-paper';
import MapPinIcon from '../Icons/MapPinIcon';
import CloseIcon from '../Icons/CloseIcon';
import BackArrowIcon from '../Icons/BackArrowIcon';
import {
  initializeGoogleMaps,
  isGoogleMapsLoaded,
} from '../../config/GoogleMaps';
import { getIsMobileDevice } from '../../utils';

const LocationPicker = ({
  value,
  onChangeText,
  onLocationSelect, // New prop to handle location data with coordinates
  label,
  placeholder,
  error,
  errorText,
  maxLength = 500,
  multiline = true,
  numberOfLines = 4,
  isVisible = false,
  onClose = null,
  hideInput = false,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [viewMode, setViewMode] = useState('search'); // 'search' or 'map'
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [mapCenter, setMapCenter] = useState({ lat: 20.5937, lng: 78.9629 }); // Default to India center
  const mapRef = useRef(null);
  const markerRef = useRef(null);

  // Use external visibility control if provided
  const modalVisible = onClose ? isVisible : isModalVisible;
  const setModalVisible = onClose ? setIsModalVisible : setIsModalVisible;

  // Maps is not loaded with the page; start loading it as soon as the picker opens.
  useEffect(() => {
    if (modalVisible && Platform.OS === 'web' && !isGoogleMapsLoaded()) {
      initializeGoogleMaps();
    }
  }, [modalVisible]);

  // Handle external visibility changes
  useEffect(() => {
    if (isVisible && onClose) {
      // External control opened the modal
      setSearchQuery('');
      setSearchResults([]);
      setViewMode('search');
      setSelectedLocation(null);

      // Pre-populate with current value if it exists
      if (value) {
        setSearchQuery(value);
        // Trigger search if there's an existing value
        setTimeout(() => {
          searchLocations(value);
        }, 100);
      }
    }
  }, [isVisible, onClose, value]);

  const openLocationPicker = () => {
    setIsModalVisible(true);
    setSearchQuery('');
    setSearchResults([]);
    setViewMode('search');
    setSelectedLocation(null);

    // Pre-populate with current value if it exists
    if (value) {
      setSearchQuery(value);
      // Trigger search if there's an existing value
      setTimeout(() => {
        searchLocations(value);
      }, 100);
    }
  };

  const closeLocationPicker = () => {
    setIsModalVisible(false);
    setSearchQuery('');
    setSearchResults([]);
    setSelectedLocation(null);

    // Call external onClose if provided
    if (onClose) {
      onClose();
    }
  };

  const handleMapClick = (event) => {
    if (viewMode === 'map' && event.latLng) {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();

      // Get address from coordinates using reverse geocoding
      if (window.google && window.google.maps) {
        // Check if Geocoding API is available
        if (!window.google.maps.Geocoder) {
          console.warn(
            'Geocoding API not available. Please enable it in Google Cloud Console.'
          );
          // Show user-friendly error message
          alert(
            'Location lookup is not available. Please enable the Geocoding API in your Google Cloud Console.'
          );
          return;
        }

        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
          if (status === 'OK' && results[0]) {
            const address = results[0].formatted_address;
            setSelectedLocation({ lat, lng, address });
            setSearchQuery(address);

            // Add a marker at the clicked location
            if (mapRef.current) {
              // Remove existing markers
              if (window.locationMarkers) {
                window.locationMarkers.forEach((marker) => {
                  if (marker.map) {
                    marker.map = null;
                  } else {
                    marker.setMap(null);
                  }
                });
              }
              window.locationMarkers = [];

              // Add new marker using AdvancedMarkerElement if available, fallback to classic Marker
              let marker;
              if (
                window.google.maps.marker &&
                window.google.maps.marker.AdvancedMarkerElement
              ) {
                marker = new window.google.maps.marker.AdvancedMarkerElement({
                  position: { lat, lng },
                  map: mapRef.current,
                  title: address,
                  gmpDraggable: true,
                });
              } else {
                // Fallback to classic marker
                marker = new window.google.maps.Marker({
                  position: { lat, lng },
                  map: mapRef.current,
                  title: address,
                  draggable: true,
                });
              }

              // Add drag listener to marker
              marker.addListener('dragend', (dragEvent) => {
                const newLat = dragEvent.latLng.lat();
                const newLng = dragEvent.latLng.lng();

                // Get address from new coordinates
                geocoder.geocode(
                  { location: { lat: newLat, lng: newLng } },
                  (reverseResults, reverseStatus) => {
                    if (reverseStatus === 'OK' && reverseResults[0]) {
                      const newAddress = reverseResults[0].formatted_address;
                      setSelectedLocation({
                        lat: newLat,
                        lng: newLng,
                        address: newAddress,
                      });
                      setSearchQuery(newAddress);
                    }
                  }
                );
              });

              window.locationMarkers.push(marker);
            }
          } else {
            setSelectedLocation({
              lat,
              lng,
              address: `Lat: ${lat.toFixed(6)}, Lng: ${lng.toFixed(6)}`,
            });
            setSearchQuery(`Lat: ${lat.toFixed(6)}, Lng: ${lng.toFixed(6)}`);
          }
        });
      }
    }
  };

  const initializeMap = () => {
    if (Platform.OS === 'web' && isGoogleMapsLoaded()) {
      try {
        // Get the actual DOM element from the ref
        const mapElement = mapRef.current;
        if (!mapElement) {
          return;
        }

        // Check if the element is actually in the DOM and visible
        if (!document.contains(mapElement) || mapElement.offsetHeight === 0) {
          return;
        }

        const map = new window.google.maps.Map(mapElement, {
          center: mapCenter,
          zoom: 10,
          mapTypeId: window.google.maps.MapTypeId.ROADMAP,
          styles: [
            {
              featureType: 'poi',
              elementType: 'labels',
              stylers: [{ visibility: 'off' }],
            },
          ],
        });

        // Add click listener to map
        map.addListener('click', handleMapClick);

        // Store map reference
        mapRef.current = map;
      } catch (error) {
        console.error('Error initializing Google Maps:', error);
      }
    } else {
    }
  };

  const toggleViewMode = () => {
    setViewMode(viewMode === 'search' ? 'map' : 'search');
    if (viewMode === 'search') {
      // Switching to map mode, initialize map
      setTimeout(() => {
        initializeMap();
      }, 100);
    }
  };

  const searchLocations = async (query) => {
    if (!query.trim() || query.length < 3) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);

    try {
      // Initialize Google Maps if not already loaded
      if (Platform.OS === 'web' && !isGoogleMapsLoaded()) {
        initializeGoogleMaps();
      }

      if (Platform.OS === 'web' && isGoogleMapsLoaded()) {
        // Use the new AutocompleteSuggestion API instead of deprecated AutocompleteService
        if (window.google.maps.places.AutocompleteSuggestion) {
          try {
            const { suggestions } =
              await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
                {
                  input: query,
                  includedPrimaryTypes: ['geocode', 'establishment'],
                  includedRegionCodes: ['IN'], // Restrict to India, you can change this
                }
              );

            if (suggestions && suggestions.length > 0) {
              // Convert suggestions to the format our component expects
              const results = suggestions.map((suggestion, index) => ({
                description:
                  suggestion.placePrediction?.text?.text ||
                  suggestion.queryPrediction?.text?.text ||
                  `Location ${index + 1}`,
                place_id:
                  suggestion.placePrediction?.placeId || `suggestion_${index}`,
                structuredFormat: suggestion.placePrediction?.structuredFormat,
              }));
              setSearchResults(results);
            } else {
              setSearchResults([]);
            }
          } catch (error) {
            console.error('AutocompleteSuggestion error:', error);
            // Fallback to manual results
            const mockResults = [
              { description: `${query} - Main Street, City`, place_id: '1' },
              { description: `${query} - Downtown Area, City`, place_id: '2' },
              {
                description: `${query} - Shopping Center, City`,
                place_id: '3',
              },
            ];
            setSearchResults(mockResults);
          }
        } else {
          // Fallback if AutocompleteSuggestion is not available
          const mockResults = [
            { description: `${query} - Main Street, City`, place_id: '1' },
            { description: `${query} - Downtown Area, City`, place_id: '2' },
            { description: `${query} - Shopping Center, City`, place_id: '3' },
          ];
          setSearchResults(mockResults);
        }
        setIsSearching(false);
      } else {
        // Fallback: simulate search results
        const mockResults = [
          { description: `${query} - Main Street, City`, place_id: '1' },
          { description: `${query} - Downtown Area, City`, place_id: '2' },
          { description: `${query} - Shopping Center, City`, place_id: '3' },
        ];
        setSearchResults(mockResults);
        setIsSearching(false);
      }
    } catch (error) {
      console.error('Error searching locations:', error);
      setIsSearching(false);
      setSearchResults([]);
    }
  };

  const selectLocation = async (result) => {
    let locationData = {
      address: '',
      latitude: null,
      longitude: null,
    };

    if (result.description) {
      locationData.address = result.description;
      onChangeText(result.description);

      // Get coordinates for the selected location
      if (window.google && window.google.maps) {
        const geocoder = new window.google.maps.Geocoder();
        try {
          const geocodeResult = await new Promise((resolve, reject) => {
            geocoder.geocode(
              { address: result.description },
              (results, status) => {
                if (status === 'OK' && results[0]) {
                  resolve(results[0]);
                } else {
                  reject(new Error('Geocoding failed'));
                }
              }
            );
          });

          const location = geocodeResult.geometry.location;
          locationData.latitude = location.lat();
          locationData.longitude = location.lng();
        } catch (error) {
          console.warn(
            'Failed to get coordinates for selected location:',
            error
          );
        }
      }
    } else if (result.address) {
      locationData.address = result.address;
      locationData.latitude = result.lat;
      locationData.longitude = result.lng;
      onChangeText(result.address);
    }

    // Call the new callback with full location data if provided
    if (onLocationSelect) {
      onLocationSelect(locationData);
    }

    closeLocationPicker();
  };

  const handleSearchChange = (text) => {
    setSearchQuery(text);
    // Debounce search
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      searchLocations(text);
    }, 500);
  };

  const searchAndShowOnMap = (query) => {
    if (!query.trim() || !window.google || !window.google.maps) {
      return;
    }

    // Check if Geocoding API is available
    if (!window.google.maps.Geocoder) {
      console.warn(
        'Geocoding API not available. Please enable it in Google Cloud Console.'
      );
      // Show user-friendly error message
      alert(
        'Location search is not available. Please enable the Geocoding API in your Google Cloud Console.'
      );
      return;
    }

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: query }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const location = results[0].geometry.location;
        const lat = location.lat();
        const lng = location.lng();

        // Update map center to the searched location
        if (mapRef.current && mapRef.current.panTo) {
          mapRef.current.panTo(location);
          mapRef.current.setZoom(15);
        }

        // Add a marker for the searched location
        if (window.google && window.google.maps) {
          // Remove existing markers
          if (window.locationMarkers) {
            window.locationMarkers.forEach((marker) => {
              if (marker.map) {
                marker.map = null;
              } else {
                marker.setMap(null);
              }
            });
          }
          window.locationMarkers = [];

          // Add new marker using AdvancedMarkerElement if available, fallback to classic Marker
          let marker;
          if (
            window.google.maps.marker &&
            window.google.maps.marker.AdvancedMarkerElement
          ) {
            marker = new window.google.maps.marker.AdvancedMarkerElement({
              position: location,
              map: mapRef.current,
              title: query,
              gmpDraggable: true,
            });
          } else {
            // Fallback to classic marker
            marker = new window.google.maps.Marker({
              position: location,
              map: mapRef.current,
              title: query,
              draggable: true,
            });
          }

          // Add click listener to marker
          marker.addListener('click', () => {
            const address = results[0].formatted_address;
            setSelectedLocation({ lat, lng, address });
            setSearchQuery(address);
          });

          // Add drag listener to marker
          marker.addListener('dragend', (event) => {
            const newLat = event.latLng.lat();
            const newLng = event.latLng.lng();

            // Get address from new coordinates
            geocoder.geocode(
              { location: { lat: newLat, lng: newLng } },
              (reverseResults, reverseStatus) => {
                if (reverseStatus === 'OK' && reverseResults[0]) {
                  const newAddress = reverseResults[0].formatted_address;
                  setSelectedLocation({
                    lat: newLat,
                    lng: newLng,
                    address: newAddress,
                  });
                  setSearchQuery(newAddress);
                }
              }
            );
          });

          window.locationMarkers.push(marker);
        }

        // Set the selected location
        const address = results[0].formatted_address;
        setSelectedLocation({ lat, lng, address });
        setSearchQuery(address);
      } else {
        // Show user-friendly error message
        alert(
          'Could not find that location. Please try a different search term.'
        );
      }
    });
  };

  const searchTimeout = React.useRef(null);

  useEffect(() => {
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, []);

  // Initialize map when switching to map mode
  useEffect(() => {
    if (viewMode === 'map' && modalVisible) {
      // Wait for the DOM element to be ready
      const timer = setTimeout(() => {
        if (mapRef.current) {
          initializeMap();
        } else {
          // Retry after a bit more time
          setTimeout(() => {
            if (mapRef.current) {
              initializeMap();
            }
          }, 300);
        }
      }, 200);

      return () => clearTimeout(timer);
    }
  }, [viewMode, modalVisible]);

  return (
    <View style={styles.container}>
      {!hideInput && (
        <View style={styles.inputContainer}>
          <TextInput
            label={label}
            value={value}
            onChangeText={onChangeText}
            placeholder={placeholder}
            error={error}
            errorText={errorText}
            maxLength={maxLength}
            multiline={multiline}
            numberOfLines={numberOfLines}
          />
          <TouchableOpacity
            onPress={openLocationPicker}
            style={[
              styles.locationButton,
              { backgroundColor: theme.colors.primary },
            ]}
          >
            <MapPinIcon size={24} color={theme.colors.white} />
          </TouchableOpacity>
        </View>
      )}

      {/* Full Screen Location Picker */}
      {modalVisible && (
        <View
          style={[
            styles.fullScreenOverlay,
            { backgroundColor: theme.colors.background },
          ]}
        >
          <View
            style={[
              styles.fullScreenContent,
              { backgroundColor: theme.colors.background },
            ]}
          >
            {/* Header with title and close button */}
            <View style={styles.fullScreenHeader}>
              <View style={styles.headerContentContainer}>
                <QText
                  style={[
                    styles.fullScreenTitle,
                    { color: theme.colors.onSurface },
                  ]}
                >
                  {t('select_location')}
                </QText>
              </View>
            </View>

            {/* Search Bar */}
            <View style={styles.fullScreenSearchContainer}>
              <View style={styles.searchContentContainer}>
                <View
                  style={[
                    styles.searchInputWrapper,
                    {
                      backgroundColor: theme.colors.surface,
                      borderColor: theme.colors.outline,
                    },
                  ]}
                >
                  <View style={styles.searchIconContainer}>
                    <MapPinIcon
                      size={16}
                      color={theme.colors.placeHolderText}
                    />
                  </View>
                  <RNTextInput
                    style={[
                      styles.fullScreenSearchInput,
                      {
                        color: theme.colors.onSurface,
                        backgroundColor: theme.colors.surface,
                      },
                    ]}
                    placeholder="Search for a location..."
                    placeholderTextColor={theme.colors.placeHolderText}
                    value={searchQuery}
                    onChangeText={handleSearchChange}
                    autoFocus={true}
                  />
                  {searchQuery.length > 0 &&
                    !(Platform.OS === 'web' && getIsMobileDevice()) && (
                      <TouchableOpacity
                        style={styles.clearButton}
                        onPress={() => setSearchQuery('')}
                      >
                        <QText
                          style={[
                            styles.clearButtonText,
                            { color: theme.colors.placeHolderText },
                          ]}
                        >
                          ✕
                        </QText>
                      </TouchableOpacity>
                    )}
                </View>
              </View>
            </View>

            {/* Select Current Location Option */}
            <View style={styles.optionsContentContainer}>
              <View style={styles.currentLocationContainer}>
                <TouchableOpacity
                  style={styles.currentLocationButton}
                  onPress={() => {
                    if (navigator.geolocation) {
                      navigator.geolocation.getCurrentPosition(
                        (position) => {
                          const lat = position.coords.latitude;
                          const lng = position.coords.longitude;

                          // Get address from coordinates using reverse geocoding
                          if (window.google && window.google.maps) {
                            if (!window.google.maps.Geocoder) {
                              console.warn('Geocoding API not available.');
                              const locationText = `Current Location (Lat: ${lat.toFixed(6)}, Lng: ${lng.toFixed(6)})`;
                              onChangeText(locationText);
                              closeLocationPicker();
                              return;
                            }

                            const geocoder = new window.google.maps.Geocoder();
                            geocoder.geocode(
                              { location: { lat, lng } },
                              (results, status) => {
                                if (status === 'OK' && results[0]) {
                                  const address = results[0].formatted_address;
                                  onChangeText(address);

                                  // Call the new callback with full location data if provided
                                  if (onLocationSelect) {
                                    onLocationSelect({
                                      address: address,
                                      latitude: lat,
                                      longitude: lng,
                                    });
                                  }

                                  closeLocationPicker();
                                } else {
                                  const locationText = `Current Location (Lat: ${lat.toFixed(6)}, Lng: ${lng.toFixed(6)})`;
                                  onChangeText(locationText);

                                  // Call the new callback with coordinates even if address is not available
                                  if (onLocationSelect) {
                                    onLocationSelect({
                                      address: locationText,
                                      latitude: lat,
                                      longitude: lng,
                                    });
                                  }

                                  closeLocationPicker();
                                }
                              }
                            );
                          } else {
                            const locationText = `Current Location (Lat: ${lat.toFixed(6)}, Lng: ${lng.toFixed(6)})`;
                            onChangeText(locationText);

                            // Call the new callback with coordinates even without Google Maps
                            if (onLocationSelect) {
                              onLocationSelect({
                                address: locationText,
                                latitude: lat,
                                longitude: lng,
                              });
                            }

                            closeLocationPicker();
                          }
                        },
                        (error) => {
                          console.error(
                            'Error getting current location:',
                            error
                          );
                          alert(
                            'Could not get your current location. Please check your browser permissions.'
                          );
                        }
                      );
                    } else {
                      alert('Geolocation is not supported by your browser.');
                    }
                  }}
                >
                  <View style={styles.currentLocationIcon}>
                    <View
                      style={[
                        styles.pinTop,
                        { backgroundColor: theme.colors.primary },
                      ]}
                    />
                    <View
                      style={[
                        styles.pinStem,
                        { backgroundColor: theme.colors.primary },
                      ]}
                    />
                  </View>
                  <QText
                    style={[
                      styles.currentLocationText,
                      { color: theme.colors.onSurface },
                    ]}
                  >
                    Select current location
                  </QText>
                </TouchableOpacity>
              </View>

              {/* Divider Line */}
              <View style={styles.dividerLine} />

              {/* Set Location on Map Option */}
              <View style={styles.mapInstructionContainer}>
                <TouchableOpacity
                  style={styles.mapInstructionButton}
                  onPress={() => setViewMode('map')}
                >
                  <View style={styles.customPinIcon}>
                    <View
                      style={[
                        styles.pinTop,
                        { backgroundColor: theme.colors.primary },
                      ]}
                    />
                    <View
                      style={[
                        styles.pinStem,
                        { backgroundColor: theme.colors.primary },
                      ]}
                    />
                  </View>
                  <QText
                    style={[
                      styles.mapInstructionText,
                      { color: theme.colors.onSurface },
                    ]}
                  >
                    Set location on map
                  </QText>
                </TouchableOpacity>
              </View>
            </View>

            {viewMode === 'search' ? (
              // Search Mode
              <>
                {Platform.OS === 'web' && !isGoogleMapsLoaded() && (
                  <View style={styles.apiWarningContainer}>
                    <QText
                      style={[
                        styles.apiWarningText,
                        { color: theme.colors.error },
                      ]}
                    >
                      {t('google_maps_api_not_loaded')}
                    </QText>
                    <QText
                      style={[
                        styles.apiWarningSubtext,
                        { color: theme.colors.outline },
                      ]}
                    >
                      {t('google_maps_api_not_loaded_subtext')}
                    </QText>
                  </View>
                )}

                <View style={styles.resultsContentContainer}>
                  <ScrollView
                    style={[
                      styles.resultsContainer,
                      styles.fullScreenResultsContainer,
                    ]}
                  >
                    {isSearching && (
                      <View style={styles.loadingContainer}>
                        <QText
                          style={[
                            styles.loadingText,
                            { color: theme.colors.onSurface },
                          ]}
                        >
                          {t('searching')}...
                        </QText>
                      </View>
                    )}

                    {!isSearching &&
                      searchResults.length === 0 &&
                      searchQuery.length >= 3 && (
                        <View style={styles.noResultsContainer}>
                          <QText style={styles.noResultsText}>
                            {t('no_locations_found')}
                          </QText>
                        </View>
                      )}

                    {searchResults.map((result, index) => (
                      <TouchableOpacity
                        key={result.place_id || index}
                        style={[
                          styles.fullScreenResultItem,
                          {
                            borderBottomColor: theme.colors.outline,
                            backgroundColor: theme.colors.surface,
                          },
                        ]}
                        onPress={() => selectLocation(result)}
                      >
                        <MapPinIcon size={16} color={theme.colors.primary} />
                        <QText
                          style={[
                            styles.fullScreenResultText,
                            { color: theme.colors.onSurface },
                          ]}
                          numberOfLines={2}
                        >
                          {result.description}
                        </QText>
                      </TouchableOpacity>
                    ))}
                  </ScrollView>
                </View>
              </>
            ) : (
              // Map Mode - Full Screen
              <View
                style={[
                  styles.fullScreenMapOverlay,
                  { backgroundColor: theme.colors.background },
                ]}
              >
                <View style={styles.fullScreenMapContent}>
                  {/* Header with Title */}
                  <View style={styles.mapHeaderContainer}>
                    <QText
                      style={[
                        styles.mapHeading,
                        { color: theme.colors.onSurface },
                      ]}
                    >
                      {t('select_location')}
                    </QText>
                  </View>

                  {/* Search Box Inside Map */}
                  <View style={styles.mapSearchContainer}>
                    <View
                      style={[
                        styles.searchInputWrapper,
                        {
                          backgroundColor: theme.colors.surface,
                          borderColor: theme.colors.outline,
                          flex: 1,
                          maxWidth: 'none',
                        },
                      ]}
                    >
                      <View style={styles.searchIconContainer}>
                        <MapPinIcon
                          size={16}
                          color={theme.colors.placeHolderText}
                        />
                      </View>
                      <RNTextInput
                        style={[
                          styles.fullScreenSearchInput,
                          {
                            color: theme.colors.onSurface,
                            backgroundColor: theme.colors.surface,
                          },
                        ]}
                        placeholder="Search for a location..."
                        placeholderTextColor={theme.colors.placeHolderText}
                        value={searchQuery}
                        onChangeText={handleSearchChange}
                      />
                      {searchQuery.length > 0 &&
                        !(Platform.OS === 'web' && getIsMobileDevice()) && (
                          <TouchableOpacity
                            style={styles.clearButton}
                            onPress={() => setSearchQuery('')}
                          >
                            <QText
                              style={[
                                styles.clearButtonText,
                                { color: theme.colors.placeHolderText },
                              ]}
                            >
                              ✕
                            </QText>
                          </TouchableOpacity>
                        )}
                    </View>
                    <TouchableOpacity
                      style={[
                        styles.mapSearchButton,
                        { backgroundColor: theme.colors.primary },
                      ]}
                      onPress={() => {
                        if (searchQuery.trim()) {
                          searchAndShowOnMap(searchQuery.trim());
                        }
                      }}
                    >
                      <QText
                        style={[
                          styles.mapSearchButtonText,
                          { color: theme.colors.white },
                        ]}
                      >
                        {t('search')}
                      </QText>
                    </TouchableOpacity>
                  </View>

                  {/* Current Location Option */}
                  <View style={styles.mapCurrentLocationContainer}>
                    <TouchableOpacity
                      style={styles.mapCurrentLocationButton}
                      onPress={() => {
                        if (navigator.geolocation) {
                          navigator.geolocation.getCurrentPosition(
                            (position) => {
                              const lat = position.coords.latitude;
                              const lng = position.coords.longitude;

                              // Get address from coordinates using reverse geocoding
                              if (window.google && window.google.maps) {
                                if (!window.google.maps.Geocoder) {
                                  console.warn('Geocoding API not available.');
                                  const locationText = `Current Location (Lat: ${lat.toFixed(6)}, Lng: ${lng.toFixed(6)})`;
                                  onChangeText(locationText);
                                  closeLocationPicker();
                                  return;
                                }

                                const geocoder =
                                  new window.google.maps.Geocoder();
                                geocoder.geocode(
                                  { location: { lat, lng } },
                                  (results, status) => {
                                    if (status === 'OK' && results[0]) {
                                      const address =
                                        results[0].formatted_address;
                                      onChangeText(address);

                                      // Call the new callback with full location data if provided
                                      if (onLocationSelect) {
                                        onLocationSelect({
                                          address: address,
                                          latitude: lat,
                                          longitude: lng,
                                        });
                                      }

                                      closeLocationPicker();
                                    } else {
                                      const locationText = `Current Location (Lat: ${lat.toFixed(6)}, Lng: ${lng.toFixed(6)})`;
                                      onChangeText(locationText);

                                      // Call the new callback with coordinates even if address is not available
                                      if (onLocationSelect) {
                                        onLocationSelect({
                                          address: locationText,
                                          latitude: lat,
                                          longitude: lng,
                                        });
                                      }

                                      closeLocationPicker();
                                    }
                                  }
                                );
                              } else {
                                const locationText = `Current Location (Lat: ${lat.toFixed(6)}, Lng: ${lng.toFixed(6)})`;
                                onChangeText(locationText);

                                // Call the new callback with coordinates even without Google Maps
                                if (onLocationSelect) {
                                  onLocationSelect({
                                    address: locationText,
                                    latitude: lat,
                                    longitude: lng,
                                  });
                                }

                                closeLocationPicker();
                              }
                            },
                            (error) => {
                              console.error(
                                'Error getting current location:',
                                error
                              );
                              alert(
                                'Could not get your current location. Please check your browser permissions.'
                              );
                            }
                          );
                        } else {
                          alert(
                            'Geolocation is not supported by your browser.'
                          );
                        }
                      }}
                    >
                      <View style={styles.mapCurrentLocationIcon}>
                        <View
                          style={[
                            styles.pinTop,
                            { backgroundColor: theme.colors.primary },
                          ]}
                        />
                        <View
                          style={[
                            styles.pinStem,
                            { backgroundColor: theme.colors.primary },
                          ]}
                        />
                      </View>
                      <QText
                        style={[
                          styles.mapCurrentLocationText,
                          { color: theme.colors.onSurface },
                        ]}
                      >
                        Current Location
                      </QText>
                    </TouchableOpacity>
                  </View>

                  {Platform.OS === 'web' ? (
                    <>
                      <div
                        ref={mapRef}
                        style={{
                          width: '100%',
                          height: 'calc(100vh - 280px)',
                          backgroundColor: '#f0f0f0',
                          borderRadius: 8,
                          marginTop: 5,
                        }}
                      />
                      {!isGoogleMapsLoaded() && (
                        <View style={styles.mapErrorContainer}>
                          <QText
                            style={[
                              styles.mapErrorText,
                              { color: theme.colors.error },
                            ]}
                          >
                            {t('map_not_available')}
                          </QText>
                          <QText
                            style={[
                              styles.mapErrorSubtext,
                              { color: theme.colors.outline },
                            ]}
                          >
                            {t('use_search_mode_instead')}
                          </QText>
                        </View>
                      )}
                    </>
                  ) : (
                    <View ref={mapRef} style={styles.map} />
                  )}

                  {/* Map Mode Bottom Buttons */}
                  <View style={styles.mapBottomButtons}>
                    <TouchableOpacity
                      style={[
                        styles.backButton,
                        {
                          backgroundColor: '#FFFFFF',
                          borderWidth: 1,
                          borderColor: '#E0E0E0',
                        },
                      ]}
                      onPress={() => setViewMode('search')}
                    >
                      <QText
                        style={[styles.backButtonText, { color: '#333333' }]}
                      >
                        Back
                      </QText>
                    </TouchableOpacity>

                    <TouchableOpacity
                      style={[
                        styles.nextButton,
                        { backgroundColor: theme.colors.primary },
                      ]}
                      onPress={() => {
                        if (selectedLocation) {
                          selectLocation(selectedLocation);
                        } else if (searchQuery.trim()) {
                          onChangeText(searchQuery.trim());

                          // Try to get coordinates for the typed location
                          if (
                            onLocationSelect &&
                            window.google &&
                            window.google.maps
                          ) {
                            const geocoder = new window.google.maps.Geocoder();
                            geocoder.geocode(
                              { address: searchQuery.trim() },
                              (results, status) => {
                                if (status === 'OK' && results[0]) {
                                  const location = results[0].geometry.location;
                                  onLocationSelect({
                                    address: searchQuery.trim(),
                                    latitude: location.lat(),
                                    longitude: location.lng(),
                                  });
                                } else {
                                  // No coordinates available for typed text
                                  onLocationSelect({
                                    address: searchQuery.trim(),
                                    latitude: null,
                                    longitude: null,
                                  });
                                }
                              }
                            );
                          }

                          closeLocationPicker();
                        }
                      }}
                    >
                      <QText
                        style={[
                          styles.nextButtonText,
                          { color: theme.colors.white },
                        ]}
                      >
                        Add Location
                      </QText>
                    </TouchableOpacity>
                  </View>
                </View>
              </View>
            )}

            {viewMode === 'search' && (
              <View style={styles.modalFooter}>
                <View style={styles.footerContentContainer}>
                  <View style={styles.helpTextContainer}>
                    <QText
                      style={[styles.helpText, { color: theme.colors.outline }]}
                    >
                      Please select or pin your location so that you are
                      locatable
                    </QText>
                  </View>
                  <TouchableOpacity
                    style={[
                      styles.fullWidthCancelButton,
                      {
                        backgroundColor: '#FFFFFF',
                        borderWidth: 1,
                        borderColor: '#E0E0E0',
                      },
                    ]}
                    onPress={closeLocationPicker}
                  >
                    <QText
                      style={[
                        styles.fullWidthCancelButtonText,
                        { color: '#333333' },
                      ]}
                    >
                      {t('cancel')}
                    </QText>
                  </TouchableOpacity>
                </View>
              </View>
            )}
          </View>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    position: 'relative',
  },
  locationButton: {
    padding: 8,
    borderRadius: 6,
    marginLeft: 8, // Add some space between text input and button
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    width: '90%',
    maxWidth: 500,
    maxHeight: '80%',
    borderRadius: 12,
    padding: 20,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  closeButton: {
    padding: 4,
  },
  viewModeToggle: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginBottom: 20,
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
    borderRadius: 8,
    padding: 4,
  },
  toggleButton: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 6,
  },
  toggleButtonText: {
    fontSize: 14,
    fontWeight: '500',
  },
  searchContainer: {
    marginBottom: 20,
  },
  searchInput: {
    height: 48,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 16,
    fontSize: 16,
  },
  apiWarningContainer: {
    marginTop: 8,
    padding: 12,
    backgroundColor: 'rgba(255, 0, 0, 0.1)',
    borderRadius: 6,
    borderLeftWidth: 4,
    borderLeftColor: '#ff0000',
  },
  apiWarningText: {
    fontSize: 14,
    fontWeight: '500',
    marginBottom: 4,
  },
  apiWarningSubtext: {
    fontSize: 12,
  },
  mapContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f0f0f0', // Light background for map
    borderRadius: 8,
    overflow: 'hidden',
  },
  mapSearchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    marginBottom: 15,
    maxWidth: 600,
    alignSelf: 'center',
    width: '100%',
    paddingHorizontal: 15,
  },
  mapSearchInput: {
    flex: 1,
    height: 40,
    paddingHorizontal: 12,
    fontSize: 14,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: 'rgba(0, 0, 0, 0.1)',
  },
  mapSearchButton: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    minHeight: 48,
    justifyContent: 'center',
    alignItems: 'center',
  },
  mapSearchButtonText: {
    fontSize: 14,
    fontWeight: '500',
  },
  currentLocationButton: {
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 6,
    minWidth: 50,
    alignItems: 'center',
  },
  currentLocationButtonText: {
    fontSize: 14,
    fontWeight: '500',
  },
  mapInstructions: {
    position: 'absolute',
    top: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    padding: 10,
    borderRadius: 8,
    zIndex: 1,
  },
  mapInstructionsText: {
    fontSize: 14,
    textAlign: 'center',
  },
  map: {
    width: '100%',
    height: '100%',
  },

  resultsContainer: {
    flex: 1,
    marginBottom: 20,
  },
  loadingContainer: {
    padding: 20,
    alignItems: 'center',
  },
  loadingText: {
    fontSize: 16,
  },
  noResultsContainer: {
    padding: 20,
    alignItems: 'center',
  },
  noResultsText: {
    fontSize: 16,
    fontStyle: 'italic',
  },
  resultItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    padding: 16,
    borderBottomWidth: 1,
    gap: 12,
  },
  resultText: {
    flex: 1,
    fontSize: 16,
    lineHeight: 22,
  },
  modalFooter: {
    alignItems: 'center',
  },
  footerButtons: {
    flexDirection: 'row',
    gap: 12,
  },
  manualButton: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderWidth: 1,
    borderRadius: 8,
    minWidth: 120,
    alignItems: 'center',
  },
  manualButtonText: {
    fontSize: 16,
    fontWeight: '500',
  },
  cancelButton: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderWidth: 1,
    borderRadius: 8,
    minWidth: 100,
    alignItems: 'center',
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '500',
  },
  confirmButton: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderWidth: 1,
    borderRadius: 8,
    minWidth: 120,
    alignItems: 'center',
  },
  confirmButtonText: {
    fontSize: 16,
    fontWeight: '500',
  },
  mapErrorContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    zIndex: 1,
  },
  mapErrorText: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 10,
  },
  mapErrorSubtext: {
    fontSize: 14,
    textAlign: 'center',
  },
  clearButton: {
    padding: Platform.OS === 'web' && getIsMobileDevice() ? 2 : 4,
    borderRadius: Platform.OS === 'web' && getIsMobileDevice() ? 8 : 10,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    marginLeft: Platform.OS === 'web' && getIsMobileDevice() ? 2 : 4,
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: Platform.OS === 'web' && getIsMobileDevice() ? 20 : 24,
    minHeight: Platform.OS === 'web' && getIsMobileDevice() ? 20 : 24,
  },
  clearButtonText: {
    fontSize: Platform.OS === 'web' && getIsMobileDevice() ? 12 : 14,
    fontWeight: 'bold',
  },
  fullScreenOverlay: {
    position: Platform.OS === 'web' ? 'fixed' : 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9999,
    display: 'flex',
    flexDirection: 'column',
  },
  fullScreenContent: {
    flex: 1,
    paddingTop: Platform.OS === 'web' ? 20 : 60,
    paddingHorizontal: 20,
    paddingBottom: 20,
  },
  fullScreenHeader: {
    alignItems: 'center',
    marginBottom: 20,
    paddingTop: Platform.OS === 'web' ? 0 : 20,
  },
  fullScreenTitle: {
    fontSize: 20,
    lineHeight: 30,
    fontWeight: '600',
  },
  fullScreenCloseButton: {
    padding: 8,
    borderRadius: 20,
  },
  fullScreenSearchContainer: {
    marginBottom: 20,
  },
  fullScreenSearchInput: {
    height: 48,
    flex: 1,
    paddingHorizontal: 0,
    fontSize: 16,
    borderWidth: 0,
    outline: 'none',
    ...(Platform.OS === 'web' && {
      outlineStyle: 'none',
      outlineWidth: 0,
      outlineColor: 'transparent',
      boxShadow: 'none',
      WebkitAppearance: 'none',
      MozAppearance: 'none',
      appearance: 'none',
    }),
  },
  searchInputWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 8,
    borderWidth: 1,
    paddingHorizontal: 12,
  },
  searchIconContainer: {
    paddingRight: 10,
  },
  searchIcon: {
    fontSize: 20,
  },
  clearButton: {
    padding: Platform.OS === 'web' && getIsMobileDevice() ? 2 : 4,
    borderRadius: Platform.OS === 'web' && getIsMobileDevice() ? 8 : 10,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    marginLeft: Platform.OS === 'web' && getIsMobileDevice() ? 2 : 4,
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: Platform.OS === 'web' && getIsMobileDevice() ? 20 : 24,
    minHeight: Platform.OS === 'web' && getIsMobileDevice() ? 20 : 24,
  },
  clearButtonText: {
    fontSize: Platform.OS === 'web' && getIsMobileDevice() ? 12 : 14,
    fontWeight: 'bold',
  },
  // Full-screen specific styles for search results and map
  fullScreenResultsContainer: {
    flex: 1,
    marginTop: 20,
  },
  fullScreenMapContainer: {
    flex: 1,
    marginTop: 20,
    minHeight: 400,
  },
  fullScreenResultItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 16,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
    marginBottom: 8,
    borderRadius: 8,
  },
  fullScreenResultText: {
    marginLeft: 12,
    fontSize: 16,
    flex: 1,
  },
  mapInstructionContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 20,
    gap: 12,
    marginBottom: 20,
  },
  mapInstructionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
  },
  mapInstructionText: {
    fontSize: 16,
    fontWeight: '500',
  },
  mapHeaderContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
    marginTop: 10,
    paddingHorizontal: 20,
  },
  mapBackButton: {
    padding: 8,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
  mapHeading: {
    fontSize: 18,
    fontWeight: '600',
    textAlign: 'center',
    flex: 1,
  },
  mapHeaderSpacer: {
    width: 40, // Same width as back button to center the title
  },
  mapCurrentLocationContainer: {
    maxWidth: 600,
    alignSelf: 'center',
    width: '100%',
    paddingHorizontal: 20,
    marginBottom: 12,
  },
  mapCurrentLocationButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    gap: 12,
  },
  mapCurrentLocationIcon: {
    width: 20,
    height: 20,
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  mapCurrentLocationText: {
    fontSize: 16,
    fontWeight: '500',
  },
  floatingActionButton: {
    position: 'absolute',
    bottom: 30,
    left: '50%',
    transform: [{ translateX: -75 }], // Half of approximate button width to center
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 30,
    elevation: 8,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.3,
    shadowRadius: 6,
    zIndex: 1000,
    minWidth: 150,
  },
  floatingActionButtonText: {
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
  },
  customPinIcon: {
    width: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  pinTop: {
    width: 8,
    height: 8,
    borderRadius: 4,
    position: 'absolute',
    top: 2,
    left: 8,
  },
  pinStem: {
    width: 2,
    height: 12,
    position: 'absolute',
    top: 10,
    left: 11,
  },
  currentLocationContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 20,
    gap: 8,
    marginBottom: 5,
  },
  currentLocationButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
  },
  currentLocationIcon: {
    width: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  currentLocationText: {
    fontSize: 16,
    fontWeight: '500',
    flex: 1,
  },
  dividerLine: {
    height: 1,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    marginVertical: 10,
    marginHorizontal: 20,
  },
  fullScreenMapOverlay: {
    position: Platform.OS === 'web' ? 'fixed' : 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9999,
    display: 'flex',
    flexDirection: 'column',
  },
  fullScreenMapContent: {
    flex: 1,
    paddingTop: Platform.OS === 'web' ? 20 : 60,
    paddingHorizontal: 20,
    paddingBottom: 20,
  },
  backButton: {
    flex: 1.2,
    paddingVertical: 10,
    paddingHorizontal: 28,
    borderRadius: 8,
    marginRight: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  backButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  nextButton: {
    flex: 1.2,
    paddingVertical: 10,
    paddingHorizontal: 28,
    borderRadius: 8,
    marginLeft: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  nextButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  mapBottomButtons: {
    flexDirection: 'row',
    paddingHorizontal: 20,
    paddingVertical: 15,
    paddingBottom: 20,
    gap: 16,
  },
  headerContentContainer: {
    maxWidth: 600,
    width: '100%',
    alignSelf: 'center',
    alignItems: 'center',
    justifyContent: 'center',
  },
  searchContentContainer: {
    maxWidth: 600,
    width: '100%',
    alignSelf: 'center',
  },
  footerContentContainer: {
    maxWidth: 600,
    width: '100%',
    alignSelf: 'center',
  },
  optionsContentContainer: {
    maxWidth: 600,
    width: '100%',
    alignSelf: 'center',
  },
  resultsContentContainer: {
    maxWidth: 600,
    width: '100%',
    alignSelf: 'center',
    flex: 1,
  },
  helpTextContainer: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    alignItems: 'center',
  },
  helpText: {
    fontSize: 14,
    textAlign: 'center',
    lineHeight: 20,
  },
  fullWidthCancelButton: {
    width: '100%',
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    marginHorizontal: 20,
  },
  fullWidthCancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
});

export default LocationPicker;
